import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, FolderGit2, Gauge, Plus, Sparkles, Trash2 } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import Footer from '../components/Footer';
import {
  OrganizationService,
  type FleetTopline,
  type OrgSummary,
} from '../services/organizationService';
import { GradeSpectrum } from '../components/fleet/FleetViz';

function ScoreBadge({ score }: { score: number | null }) {
  if (score === null) {
    return <span className="text-sm text-gray-400">not scanned</span>;
  }
  const tone =
    score >= 70
      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
      : score >= 40
        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
        : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-sm font-semibold tabular-nums ${tone}`}>
      {score}
    </span>
  );
}

function TopBucket({ label, count, total }: { label: string; count: number; total: number }) {
  const pct = total ? Math.round((count / total) * 100) : 0;
  const tone =
    label === 'F' || label === 'D'
      ? 'text-red-600 dark:text-red-400'
      : label === 'C'
        ? 'text-yellow-600 dark:text-yellow-400'
        : 'text-green-600 dark:text-green-400';
  return (
    <div className="flex-1 text-center">
      <div className={`text-xl font-bold tracking-tight tabular-nums ${tone}`}>
        {count.toLocaleString()}
      </div>
      <div className="mt-0.5 text-[11px] font-medium text-gray-500 dark:text-gray-400">
        {label} · {pct}%
      </div>
    </div>
  );
}

function HeroTiles({ topline }: { topline: FleetTopline }) {
  const total = topline.analyzedCount || 1;
  const tiles = [
    {
      icon: FolderGit2,
      value: topline.repoCount.toLocaleString(),
      label: `repositories · ${topline.orgCount} org${topline.orgCount === 1 ? '' : 's'}`,
      accent: false,
    },
    { icon: Gauge, value: topline.meanScore ?? '—', label: 'mean health score', accent: false },
    {
      icon: Sparkles,
      value: topline.maintainedRepos.toLocaleString(),
      label: 'being maintained by Autar',
      accent: true,
    },
  ];
  return (
    <div className="relative mt-6 grid gap-3 sm:grid-cols-5">
      {tiles.map((t) => {
        const Icon = t.icon;
        return (
          <div
            key={t.label}
            className={`group rounded-2xl border p-4 transition-all hover:-translate-y-0.5 ${
              t.accent
                ? 'border-teal-200/70 dark:border-teal-900 bg-teal-50/70 dark:bg-teal-950/30'
                : 'border-gray-200/70 dark:border-gray-800 bg-white/70 dark:bg-gray-900/40'
            }`}
          >
            <Icon
              className={`w-4 h-4 mb-2 ${t.accent ? 'text-teal-500' : 'text-gray-400 dark:text-gray-500'}`}
            />
            <div
              className={`text-3xl font-bold tracking-tight tabular-nums ${
                t.accent ? 'text-teal-600 dark:text-teal-400' : 'text-gray-900 dark:text-white'
              }`}
            >
              {t.value}
            </div>
            <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{t.label}</div>
          </div>
        );
      })}
      <div className="sm:col-span-2 flex items-center rounded-2xl border border-gray-200/70 dark:border-gray-800 bg-white/70 dark:bg-gray-900/40 p-4 divide-x divide-gray-100 dark:divide-gray-800">
        {topline.scoreBuckets.map((b) => (
          <TopBucket key={b.label} label={b.label} count={b.count} total={total} />
        ))}
      </div>
    </div>
  );
}

function CardGridSkeleton() {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className="animate-pulse rounded-2xl border border-gray-200/70 dark:border-gray-800 bg-white dark:bg-gray-900/40 p-5"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="h-5 w-36 rounded bg-gray-200 dark:bg-gray-800" />
            <div className="h-5 w-10 rounded-full bg-gray-200 dark:bg-gray-800" />
          </div>
          <div className="h-10 rounded bg-gray-100 dark:bg-gray-800/60 mb-4" />
          <div className="grid grid-cols-3 gap-2 mb-4">
            {[0, 1, 2].map((j) => (
              <div key={j} className="h-12 rounded-xl bg-gray-100 dark:bg-gray-800/60" />
            ))}
          </div>
          <div className="h-3 w-3/4 rounded bg-gray-100 dark:bg-gray-800/60 mb-2" />
          <div className="h-3 w-1/2 rounded bg-gray-100 dark:bg-gray-800/60" />
        </div>
      ))}
    </div>
  );
}

const inputClass =
  'px-3.5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900/60 text-sm text-gray-900 dark:text-white outline-none transition-colors focus:ring-2 focus:ring-teal-500/40 focus:border-teal-400';

export const OrganizationsPage: React.FC = () => {
  const [orgs, setOrgs] = useState<OrgSummary[]>([]);
  const [topline, setTopline] = useState<FleetTopline | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPlatform, setNewPlatform] = useState('github');
  const [importNow, setImportNow] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await OrganizationService.list();
      setOrgs(res.data.organizations);
      setTopline(res.data.topline);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (e: React.MouseEvent, org: OrgSummary) => {
    // The card is a Link — keep the click from navigating.
    e.preventDefault();
    e.stopPropagation();
    const ok = window.confirm(
      `Delete organization "${org.name}"? Its ${org.repoCount} repositories are detached, not deleted.`,
    );
    if (!ok) return;
    try {
      await OrganizationService.delete(org.id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete organization');
    }
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setSubmitting(true);
    setNotice(null);
    try {
      const created = await OrganizationService.create({ name, platform: newPlatform });
      // One-step take-on: pull the org's public repos right away.
      const canImport = newPlatform === 'github' || newPlatform === 'gitlab';
      if (importNow && canImport) {
        try {
          const res = await OrganizationService.importFromPlatform(
            created.data.id,
            newPlatform,
            name.toLowerCase(),
          );
          setNotice(`Created ${name} and imported ${res.data.fetched} public repositories.`);
        } catch (impErr) {
          const msg = impErr instanceof Error ? impErr.message : 'import failed';
          setNotice(
            msg.includes('404')
              ? `Created ${name}, but its repositories are not publicly listable — the org/group is private or doesn't exist on ${newPlatform}.com. Private orgs need the OAuth connect (coming soon); you can attach analyzed repos from the fleet view meanwhile.`
              : `Created ${name}, but the import failed: ${msg}`,
          );
        }
      } else {
        setNotice(`Created ${name}. Open its fleet view to add repositories.`);
      }
      setNewName('');
      setAdding(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create organization');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50/40 dark:bg-transparent">
      <Navbar pageTitle="Organizations" showDivider={true} />
      <main className="pt-(--navbar-offset) max-w-5xl mx-auto px-4 pb-20">
        {/* Hero header */}
        <div className="relative mt-8 mb-6 overflow-hidden rounded-3xl border border-gray-200/70 dark:border-gray-800 bg-gradient-to-br from-teal-50 via-white to-white dark:from-teal-950/30 dark:via-gray-900/40 dark:to-gray-900/40 p-6 sm:p-8">
          <div className="relative flex items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                Managed Organizations
              </h1>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Each organization is a fleet of repositories Autar maintains.
              </p>
            </div>
            <button
              onClick={() => setAdding((v) => !v)}
              className="shrink-0 inline-flex items-center gap-2 rounded-xl bg-teal-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:bg-teal-700 hover:shadow active:scale-[.97]"
            >
              <Plus className="w-4 h-4" /> Add organization
            </button>
          </div>

          {loading ? (
            <div className="relative mt-6 grid gap-3 sm:grid-cols-5 animate-pulse">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="h-24 rounded-2xl border border-gray-200/70 dark:border-gray-800 bg-white/70 dark:bg-gray-900/40"
                />
              ))}
              <div className="sm:col-span-2 h-24 rounded-2xl border border-gray-200/70 dark:border-gray-800 bg-white/70 dark:bg-gray-900/40" />
            </div>
          ) : (
            topline && topline.repoCount > 0 && <HeroTiles topline={topline} />
          )}
        </div>

        {adding && (
          <form
            onSubmit={handleAdd}
            className="mb-6 rounded-2xl border border-gray-200/70 dark:border-gray-800 bg-white dark:bg-gray-900/40 shadow-sm p-5"
          >
            <div className="flex flex-wrap items-end gap-3">
              <label className="block">
                <span className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">
                  Provider
                </span>
                <select
                  value={newPlatform}
                  onChange={(e) => setNewPlatform(e.target.value)}
                  className={`w-44 shrink-0 ${inputClass}`}
                >
                  <option value="github">GitHub</option>
                  <option value="gitlab">GitLab</option>
                  <option value="bitbucket">Bitbucket</option>
                  <option value="azure-devops">Azure DevOps</option>
                </select>
              </label>
              <label className="block min-w-48 flex-1">
                <span className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">
                  Organization name
                </span>
                <input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g. dowjones"
                  autoFocus
                  className={`w-full min-w-0 ${inputClass}`}
                />
              </label>
              <button
                type="submit"
                disabled={submitting || !newName.trim()}
                className="shrink-0 rounded-xl bg-teal-600 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:bg-teal-700 hover:shadow active:scale-[.97] disabled:opacity-50"
              >
                {submitting ? 'Adding…' : 'Add organization'}
              </button>
            </div>
            <label className="mt-3 flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
              <input
                type="checkbox"
                checked={importNow}
                onChange={(e) => setImportNow(e.target.checked)}
                className="h-4 w-4 shrink-0 accent-teal-600"
                disabled={newPlatform !== 'github' && newPlatform !== 'gitlab'}
              />
              Import its public repositories now (GitHub / GitLab, read-only)
            </label>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Private orgs arrive through your connected provider (OAuth) and group themselves by
              namespace. Public import covers orgs you have no connection to. Collections can mix
              providers.
            </p>
          </form>
        )}

        {error && <p className="text-red-500 mb-4">{error}</p>}
        {notice && (
          <p className="mb-4 rounded-xl border border-teal-200/70 dark:border-teal-900/60 bg-teal-50/60 dark:bg-teal-950/20 px-4 py-3 text-sm text-teal-800 dark:text-teal-300">
            {notice}
          </p>
        )}

        {loading ? (
          <CardGridSkeleton />
        ) : orgs.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 text-center py-20 text-gray-500 dark:text-gray-400">
            <Building2 className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <p>No organizations yet. Add one to start a fleet take-on.</p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {orgs.map((org) => (
              <Link
                key={org.id}
                to={`/orgs/${org.id}/fleet`}
                className="block p-5 rounded-2xl border border-gray-200/70 dark:border-gray-800 bg-white dark:bg-gray-900/40 shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5 hover:border-teal-300/70 dark:hover:border-teal-800"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2.5">
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-100 dark:bg-teal-900/30">
                      <Building2 className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                    </span>
                    <span className="font-semibold text-gray-900 dark:text-white">{org.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <ScoreBadge score={org.meanScore} />
                    <button
                      onClick={(e) => handleDelete(e, org)}
                      title={`Delete ${org.name} (repos are detached, not deleted)`}
                      className="rounded-lg p-1.5 text-gray-300 dark:text-gray-600 transition-colors hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-950/40 dark:hover:text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {/* Grade composition — honest under heavy skew */}
                <div className="mb-4">
                  <GradeSpectrum buckets={org.scoreBuckets} height={10} />
                </div>

                {/* Stat grid */}
                <div className="grid grid-cols-4 gap-2 mb-4 text-center">
                  <div className="rounded-xl bg-gray-50 dark:bg-gray-800/40 py-2">
                    <div className="text-lg font-bold tracking-tight tabular-nums text-gray-900 dark:text-white">
                      {org.repoCount.toLocaleString()}
                    </div>
                    <div className="text-[10px] text-gray-500 dark:text-gray-400">repos</div>
                  </div>
                  <div className="rounded-xl bg-gray-50 dark:bg-gray-800/40 py-2">
                    <div className="text-lg font-bold tracking-tight tabular-nums text-gray-900 dark:text-white">
                      {org.totalPlanItems.toLocaleString()}
                    </div>
                    <div className="text-[10px] text-gray-500 dark:text-gray-400">
                      maintenance items
                    </div>
                  </div>
                  <div className="rounded-xl bg-teal-50 dark:bg-teal-950/30 py-2">
                    <div className="text-lg font-bold tracking-tight tabular-nums text-teal-600 dark:text-teal-400">
                      {org.automatableItems.toLocaleString()}
                    </div>
                    <div className="text-[10px] text-gray-500 dark:text-gray-400">automatable</div>
                  </div>
                  <div
                    className="rounded-xl bg-teal-50 dark:bg-teal-950/30 py-2"
                    title="Recurring: dependency-bump + lint/format cadence mined from each repo's git history, annualized at 0.5h per manual occurrence. Backlog: one-time effort over plan items Autar can execute without LLMs (trivial 0.5h · small 2h · medium 6h · large 16h)."
                  >
                    <div className="text-lg font-bold tracking-tight tabular-nums text-teal-600 dark:text-teal-400">
                      ≈{org.recurringHoursPerYear.toLocaleString()}h/yr
                    </div>
                    <div className="text-[10px] text-gray-500 dark:text-gray-400">
                      recurring upkeep · +{org.automatableHoursSaved.toLocaleString()}h backlog
                    </div>
                  </div>
                </div>

                <div className="text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
                  <p>
                    {org.analyzedCount} analyzed · {org.maintainedRepos} being maintained by Autar
                    {org.monorepoCount > 0 ? ` · ${org.monorepoCount} monorepos` : ''}
                  </p>
                  <p>
                    {/* Orgs are cross-provider collections — show the real mix. */}
                    {org.providers.length > 0
                      ? org.providers.map((p) => `${p.platform} ${p.count}`).join(' · ')
                      : org.platform}{' '}
                    ·{' '}
                    {org.settings?.deliveryMode === 'local_patch'
                      ? 'local patch delivery (read-only upstream)'
                      : 'PR delivery'}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};
